import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Create axios instance
const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || '/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Token storage keys
const ACCESS_TOKEN_KEY = 'dryvox_access_token';
const REFRESH_TOKEN_KEY = 'dryvox_refresh_token';

// Token management functions
export const getAccessToken = (): string | null => {
  return localStorage.getItem(ACCESS_TOKEN_KEY);
};

export const getRefreshToken = (): string | null => {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
};

export const setTokens = (accessToken: string, refreshToken: string): void => {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
};

export const clearTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
};

// Request interceptor - add auth header
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = getAccessToken();
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle token refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: AxiosError | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 errors
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // Wait for token refresh
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = getRefreshToken();

      if (!refreshToken) {
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(error);
      }

      try {
        const response = await axios.post(
          `${import.meta.env.VITE_API_URL || '/api/v1'}/auth/refresh`,
          { refresh_token: refreshToken }
        );

        const { access_token, refresh_token: newRefreshToken } = response.data;
        setTokens(access_token, newRefreshToken);

        processQueue(null, access_token);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`;
        }

        return api(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as AxiosError, null);
        clearTokens();
        window.location.href = '/login';
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export default api;
export { api };

// API Types
export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name?: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

export interface User {
  id: string;
  email: string;
  name: string | null;
  role: 'user' | 'admin';
  tier: 'basic' | 'standard' | 'pro' | 'elite';
  experience_level: 'trainee' | 'junior' | 'senior';
  is_active: boolean;
  has_memory: boolean;
  memory_length: number;
  created_at: string;
  last_login_at: string | null;

  // Profile fields (BUG-005)
  company: string | null;
  job_title: string | null;

  // Voice settings (BUG-006)
  voice_speed: number;
  voice_pitch: number;
  auto_listen: boolean;
  silence_detection: boolean;
  noise_reduction: boolean;

  // Notification settings (BUG-007)
  email_alerts: boolean;
  push_notifications: boolean;
  session_reminders: boolean;
  market_updates: boolean;
}

export interface UserUpdate {
  name?: string;
  password?: string;
  experience_level?: 'trainee' | 'junior' | 'senior';
  clear_memory?: boolean;

  // Profile fields (BUG-005)
  company?: string;
  job_title?: string;

  // Voice settings (BUG-006)
  voice_speed?: number;
  voice_pitch?: number;
  auto_listen?: boolean;
  silence_detection?: boolean;
  noise_reduction?: boolean;

  // Notification settings (BUG-007)
  email_alerts?: boolean;
  push_notifications?: boolean;
  session_reminders?: boolean;
  market_updates?: boolean;
}

export interface Session {
  id: string;
  user_id: string;
  user_level: 'trainee' | 'junior' | 'senior' | null;
  started_at: string;
  ended_at: string | null;
  duration_seconds: number | null;
  title: string | null;
  summary: string | null;
}

export interface Message {
  id: string;
  role: 'user' | 'assistant' | 'tool';
  content: string;
  timestamp: string;
}

export interface SessionDetail extends Session {
  messages: Message[];
}

// API functions
export const authApi = {
  login: (data: LoginRequest) => 
    api.post<TokenResponse>('/auth/login', data),
  
  register: (data: RegisterRequest) => 
    api.post<User>('/auth/register', data),
  
  refresh: (refreshToken: string) => 
    api.post<TokenResponse>('/auth/refresh', { refresh_token: refreshToken }),
  
  logout: () => 
    api.post('/auth/logout'),
};

export const userApi = {
  getMe: () =>
    api.get<User>('/users/me'),

  updateMe: (data: UserUpdate) =>
    api.patch<User>('/users/me', data),

  getStats: () =>
    api.get('/users/me/stats'),

  // Export all user data (BUG-009 fix)
  exportData: () =>
    api.get('/users/me/export'),
};

export const sessionApi = {
  list: (skip = 0, limit = 20) => 
    api.get<Session[]>(`/sessions/?skip=${skip}&limit=${limit}`),
  
  get: (id: string) => 
    api.get<SessionDetail>(`/sessions/${id}/`),
  
  create: (userLevel?: string) => 
    api.post<Session>('/sessions/', { user_level: userLevel }),
  
  update: (id: string, data: Partial<Session>) => 
    api.patch<Session>(`/sessions/${id}/`, data),
  
  addMessage: (sessionId: string, role: string, content: string) => 
    api.post<Message>(`/sessions/${sessionId}/messages/`, { role, content }),
  
  export: (id: string, format: 'txt' | 'json' = 'txt') => 
    api.get(`/sessions/${id}/export/?format=${format}`),
};

// Generated Image interfaces
export interface GeneratedImage {
  id: string;
  prompt: string;
  aspect_ratio: string;
  mime_type: string;
  image_base64: string;
  created_at: string;
}

export interface ImageGenerateResponse {
  success: boolean;
  image_id?: string;
  image_base64?: string;
  mime_type?: string;
  error?: string;
}

export const imageApi = {
  generate: (prompt: string, aspectRatio: string = '16:9') => 
    api.post<ImageGenerateResponse>('/image/generate/', { prompt, aspect_ratio: aspectRatio }),
  
  history: (skip = 0, limit = 20) => 
    api.get<GeneratedImage[]>(`/image/history/?skip=${skip}&limit=${limit}`),
  
  delete: (id: string) => 
    api.delete(`/image/${id}/`),
};

// ==============================================================================
// GENERATED DOCUMENTS API (EPIC-17)
// ==============================================================================

export interface GeneratedDocumentItem {
  id: string;
  document_type: string;
  title: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string | null;
}

export const documentsApi = {
  history: (skip = 0, limit = 20) =>
    api.get<GeneratedDocumentItem[]>(`/documents/generated/history?skip=${skip}&limit=${limit}`),

  download: async (id: string): Promise<void> => {
    const { data } = await api.get<{ filename: string; mime_type: string; data: string }>(
      `/documents/generated/${id}/download`
    );
    const byteCharacters = atob(data.data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: data.mime_type });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = data.filename;
    link.click();
    URL.revokeObjectURL(link.href);
  },

  delete: (id: string) => api.delete(`/documents/generated/${id}`),
};

// ==============================================================================
// CALCULATOR API
// ==============================================================================

// Calculator Types
export interface CalculationStep {
  description: string;
  formula: string;
  result: string;
}

export interface CalculationResponse {
  success: boolean;
  calculation_type: string;
  result: Record<string, unknown>;
  formula: string;
  inputs: Record<string, unknown>;
  steps: CalculationStep[];
  warnings: string[];
  calculated_at: string;
}

export interface PortInfo {
  code: string;
  name: string;
  country: string;
  aliases: string[];
}

// Distance
export interface DistanceRequest {
  origin_port: string;
  destination_port: string;
  speed_knots: number;
  weather_margin_pct: number;
  departure_datetime?: string;
}

// Laytime
export type RateType = 'SHINC' | 'SHEX' | 'SHEX_UU' | 'SHEX_EIU' | 'WWD' | 'CQD';

export interface LaytimeRequest {
  cargo_quantity_mt: number;
  loading_rate_per_day: number;
  rate_type: RateType;
  nor_tendered?: string;
  notice_hours?: number;
}

// Demurrage
export type DespatchType = 'DATS' | 'DWTS';

export interface TimeDeduction {
  description: string;
  hours: number;
  reason: string;
}

export interface DemurrageRequest {
  allowed_laytime_days: number;
  actual_time_days: number;
  demurrage_rate_per_day: number;
  despatch_rate_per_day?: number;
  despatch_type?: DespatchType;
  time_deductions?: TimeDeduction[];
  escalating_rate_after_days?: number;
  escalated_rate_per_day?: number;
}

// Freight
export interface CommissionEntry {
  name: string;
  percentage: number;
}

export interface FreightRequest {
  cargo_quantity_mt: number;
  freight_rate_per_mt: number;
  is_lumpsum?: boolean;
  lumpsum_amount?: number;
  address_commission_pct?: number;
  brokerage_commission_pct?: number;
  additional_commissions?: CommissionEntry[];
}

// Deadfreight
export interface DeadfreightRequest {
  contracted_quantity_mt: number;
  actual_shipped_mt: number;
  freight_rate_per_mt: number;
  owner_cost_savings_usd?: number;
}

// TCE
export interface TCERequest {
  gross_freight_usd: number;
  commission_pct: number;
  voyage_days: number;
  sea_days: number;
  port_days: number;
  laden_consumption_mt_day: number;
  ballast_consumption_mt_day: number;
  port_consumption_mt_day: number;
  fuel_price_usd_mt: number;
  port_costs_usd?: number;
  canal_dues_usd?: number;
  other_voyage_costs_usd?: number;
}

// Break Even
export interface BreakEvenRequest {
  target_tce_per_day: number;
  voyage_days: number;
  voyage_costs_usd: number;
  cargo_quantity_mt: number;
  commission_pct: number;
}

// Bunkers
export interface BunkerRequest {
  laden_days: number;
  ballast_days: number;
  port_days: number;
  laden_consumption_mt_day: number;
  ballast_consumption_mt_day: number;
  port_consumption_mt_day: number;
  vlsfo_price_usd_mt?: number;
  mgo_price_usd_mt?: number;
  mgo_consumption_mt_day?: number;
}

// Calculator API functions
export const calculatorApi = {
  // Port search
  searchPorts: (query: string, limit = 10) =>
    api.get<PortInfo[]>(`/calculator/ports/search?q=${encodeURIComponent(query)}&limit=${limit}`),

  getPort: (portCode: string) =>
    api.get<PortInfo>(`/calculator/ports/${encodeURIComponent(portCode)}`),

  // Distance
  calculateDistance: (data: DistanceRequest) =>
    api.post<CalculationResponse>('/calculator/distance', data),

  // Laytime
  calculateLaytime: (data: LaytimeRequest) =>
    api.post<CalculationResponse>('/calculator/laytime', data),

  // Demurrage
  calculateDemurrage: (data: DemurrageRequest) =>
    api.post<CalculationResponse>('/calculator/demurrage', data),

  // Freight
  calculateFreight: (data: FreightRequest) =>
    api.post<CalculationResponse>('/calculator/freight', data),

  // Deadfreight
  calculateDeadfreight: (data: DeadfreightRequest) =>
    api.post<CalculationResponse>('/calculator/deadfreight', data),

  // TCE
  calculateTCE: (data: TCERequest) =>
    api.post<CalculationResponse>('/calculator/tce', data),

  // Break Even
  calculateBreakEven: (data: BreakEvenRequest) =>
    api.post<CalculationResponse>('/calculator/break-even', data),

  // Bunkers
  calculateBunkers: (data: BunkerRequest) =>
    api.post<CalculationResponse>('/calculator/bunkers', data),
};

// Build timestamp: 20250108
